import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Link, useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { Loader2, Phone, Mail, ArrowLeft } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

// Facebook SDK App ID
const FB_APP_ID = process.env.REACT_APP_FB_APP_ID || '';

export function SignUpPage() {
  const navigate = useNavigate();
  const { register, facebookAuth, sendOTP, verifyOTP, isAuthenticated } = useAuth();
  
  const [signupMethod, setSignupMethod] = useState('phone'); // 'phone', 'email', 'otp-verify'
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: ''
  });
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [whatsappConsent, setWhatsappConsent] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  const otpRefs = useRef([]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  // Resend timer countdown
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  // Initialize Facebook SDK
  useEffect(() => {
    window.fbAsyncInit = function() {
      window.FB.init({
        appId: FB_APP_ID,
        cookie: true,
        xfbml: true,
        version: 'v19.0'
      });
    };

    if (!document.getElementById('facebook-jssdk')) {
      const script = document.createElement('script');
      script.id = 'facebook-jssdk';
      script.src = 'https://connect.facebook.net/en_US/sdk.js';
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);
    }
  }, []);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.id]: e.target.value });
    setError('');
  };

  const handleEmailSignup = async (e) => {
    e.preventDefault();
    setError('');

    if (!termsAccepted) {
      setError('Please accept the Terms of Service and Privacy Policy');
      return;
    }

    if (!formData.fullName || !formData.email || !formData.password) {
      setError('Please fill in all required fields');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setIsLoading(true);

    try {
      await register({
        full_name: formData.fullName,
        email: formData.email,
        phone: formData.phone || null,
        password: formData.password
      });
      navigate('/dashboard');
    } catch (err) {
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.phone) {
      setError('Please enter your phone number');
      return;
    }

    setIsLoading(true);

    try {
      await sendOTP(formData.phone);
      setSignupMethod('otp-verify');
      setResendTimer(60);
    } catch (err) {
      setError(err.message || 'Failed to send OTP');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOTP = async (otpOverride) => {
    const otpCode = (otpOverride || otp).join('');
    if (otpCode.length !== 6) {
      setError('Please enter the complete 6-digit OTP');
      return;
    }

    setError('');
    setIsLoading(true);

    try {
      await verifyOTP(formData.phone, otpCode);
      navigate('/dashboard');
    } catch (err) {
      setError(err.message || 'Invalid OTP');
      setOtp(['', '', '', '', '', '']);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendTimer > 0) return;
    
    setError('');
    setIsLoading(true);

    try {
      await sendOTP(formData.phone);
      setResendTimer(60);
      setOtp(['', '', '', '', '', '']);
    } catch (err) {
      setError(err.message || 'Failed to resend OTP');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }

    if (newOtp.every(d => d !== '') && newOtp.join('').length === 6) {
      setTimeout(() => handleVerifyOTP(newOtp), 100);
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text/plain').replace(/\D/g, '').slice(0, 6);
    if (pastedData.length > 0) {
      const newOtp = [...otp];
      pastedData.split('').forEach((digit, idx) => {
        if (idx < 6) newOtp[idx] = digit;
      });
      setOtp(newOtp);
      
      if (pastedData.length === 6) {
        setTimeout(() => handleVerifyOTP(pastedData.split('')), 100);
      }
    }
  };

  const handleFacebookSignup = () => {
    setError('');
    setIsLoading(true);

    if (window.FB) {
      window.FB.login((response) => {
        if (response.authResponse) {
          const authResponse = response.authResponse;
          window.FB.api('/me', { fields: 'id,name,email,picture.type(large)' }, (profile) => {
            facebookAuth(
              authResponse.accessToken,
              authResponse.userID,
              {
                name: profile.name,
                email: profile.email,
                picture: profile.picture?.data?.url
              }
            ).then(() => {
              navigate('/dashboard');
            }).catch((err) => {
              setError(err.message || 'Facebook signup failed');
              setIsLoading(false);
            });
          });
        } else {
          setError('Facebook login cancelled');
          setIsLoading(false);
        }
      }, { scope: 'email,public_profile' });
    } else {
      setError('Facebook SDK not loaded. Please refresh and try again.');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="signup-page">
      <Header />
      
      <div className="flex-1 bg-gradient-to-b from-[#E8F9EE] to-white py-12 sm:py-20 px-4">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-6 sm:mb-8">
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2 sm:mb-3">Create your account</h1>
            <p className="text-gray-600 text-sm sm:text-base">Get started with GiriChat today</p>
          </div>

          <Card className="p-6 sm:p-8 border border-gray-200 shadow-lg bg-white">
            {/* Facebook Signup */}
            <Button
              type="button"
              onClick={handleFacebookSignup}
              disabled={isLoading}
              className="w-full bg-[#1877F2] hover:bg-[#166FE5] text-white gap-3 py-6 mb-4"
              data-testid="signup-facebook-btn"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
              Continue with Facebook
            </Button>

            <div className="relative mb-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">Or sign up with</span>
              </div>
            </div>

            {/* Signup Method Toggle */}
            {signupMethod !== 'otp-verify' && (
              <div className="flex gap-2 mb-6">
                <Button
                  type="button"
                  variant={signupMethod === 'phone' ? 'default' : 'outline'}
                  className={`flex-1 ${signupMethod === 'phone' ? 'bg-[#00D95F] hover:bg-[#00C054] text-white' : ''}`}
                  onClick={() => { setSignupMethod('phone'); setError(''); }}
                  data-testid="signup-method-phone"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  WhatsApp OTP
                </Button>
                <Button
                  type="button"
                  variant={signupMethod === 'email' ? 'default' : 'outline'}
                  className={`flex-1 ${signupMethod === 'email' ? 'bg-[#00D95F] hover:bg-[#00C054] text-white' : ''}`}
                  onClick={() => { setSignupMethod('email'); setError(''); }}
                  data-testid="signup-method-email"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Email
                </Button>
              </div>
            )}

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                {error}
              </div>
            )}

            {/* Email Signup Form */}
            {signupMethod === 'email' && (
              <form onSubmit={handleEmailSignup} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="fullName" className="text-gray-900">Full Name</Label>
                  <Input
                    id="fullName"
                    name="fullName"
                    type="text"
                    autoComplete="name"
                    placeholder="John Doe"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    data-testid="signup-fullname-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-900">Email address</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    value={formData.email}
                    onChange={handleInputChange}
                    data-testid="signup-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-gray-900">Phone Number (Optional)</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    autoComplete="tel"
                    placeholder="+91 9876543210"
                    value={formData.phone}
                    onChange={handleInputChange}
                    data-testid="signup-phone-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-gray-900">Password</Label>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="new-password"
                    placeholder="Create a strong password"
                    value={formData.password}
                    onChange={handleInputChange}
                    data-testid="signup-password-input"
                  />
                  <p className="text-xs text-gray-500">Must be at least 8 characters long</p>
                </div>

                <div className="flex items-start gap-3">
                  <Checkbox 
                    id="terms" 
                    checked={termsAccepted}
                    onCheckedChange={setTermsAccepted}
                    className="mt-1"
                    data-testid="signup-terms-checkbox"
                  />
                  <label htmlFor="terms" className="text-sm text-gray-700 leading-relaxed">
                    I agree to the{' '}
                    <Link to="/terms" className="text-[#00D95F] hover:underline">
                      Terms of Service
                    </Link>{' '}
                    and{' '}
                    <Link to="/privacy" className="text-[#00D95F] hover:underline">
                      Privacy Policy
                    </Link>
                  </label>
                </div>

                <div className="flex items-start gap-3">
                  <Checkbox 
                    id="whatsapp-consent" 
                    checked={whatsappConsent}
                    onCheckedChange={setWhatsappConsent}
                    className="mt-1"
                    data-testid="signup-whatsapp-consent-checkbox"
                  />
                  <label htmlFor="whatsapp-consent" className="text-sm text-gray-700 leading-relaxed">
                    I agree to receive WhatsApp messages from GiriChat and 6Miles. These messages are related to my account or service requests. I understand that I can opt out at any time.
                  </label>
                </div>

                <Button 
                  type="submit" 
                  disabled={isLoading}
                  className="w-full bg-[#00D95F] hover:bg-[#00C054] text-white"
                  data-testid="signup-submit-btn"
                >
                  {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Account'}
                </Button>
              </form>
            )}

            {/* Phone/OTP Signup Form */}
            {signupMethod === 'phone' && (
              <form onSubmit={handleSendOTP} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="phoneSignup" className="text-gray-900">WhatsApp Number</Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="+91 9876543210"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    data-testid="signup-phone-otp-input"
                  />
                  <p className="text-xs text-gray-500">We'll send a 6-digit OTP via WhatsApp to verify your number</p>
                </div>

                <Button 
                  type="submit" 
                  disabled={isLoading || !formData.phone}
                  className="w-full bg-[#00D95F] hover:bg-[#00C054] text-white"
                  data-testid="signup-send-otp-btn"
                >
                  {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send OTP'}
                </Button>
              </form>
            )}

            {/* OTP Verification */}
            {signupMethod === 'otp-verify' && (
              <div className="space-y-4">
                <Button
                  type="button"
                  variant="ghost"
                  className="text-sm text-gray-600 p-0 h-auto"
                  onClick={() => { setSignupMethod('phone'); setOtp(['', '', '', '', '', '']); setError(''); }}
                >
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Change number
                </Button>

                <div className="text-center">
                  <p className="text-gray-600 mb-2">Enter the 6-digit code sent via WhatsApp to</p>
                  <p className="font-semibold text-gray-900">{formData.phone}</p>
                </div>

                <div className="flex gap-2 justify-center" onPaste={handleOtpPaste}>
                  {otp.map((digit, index) => (
                    <Input
                      key={index}
                      ref={(el) => otpRefs.current[index] = el}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(index, e)}
                      className="w-12 h-12 text-center text-xl font-bold"
                      data-testid={`signup-otp-input-${index}`}
                    />
                  ))}
                </div>

                <Button 
                  type="button"
                  onClick={handleVerifyOTP}
                  disabled={isLoading || otp.some(d => d === '')}
                  className="w-full bg-[#00D95F] hover:bg-[#00C054] text-white"
                  data-testid="signup-verify-otp-btn"
                >
                  {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify & Create Account'}
                </Button>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={handleResendOTP}
                    disabled={resendTimer > 0 || isLoading}
                    className={`text-sm ${resendTimer > 0 ? 'text-gray-400' : 'text-[#00D95F] hover:underline'}`}
                  >
                    {resendTimer > 0 ? `Resend OTP in ${resendTimer}s` : 'Resend OTP'}
                  </button>
                </div>
              </div>
            )}

            <div className="mt-6 text-center text-sm text-gray-600">
              Already have an account?{' '}
              <Link to="/login" className="text-[#00D95F] hover:underline font-semibold">
                Sign in
              </Link>
            </div>
          </Card>

          {/* Meta Compliance Notice */}
          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              By creating an account, you agree to comply with{' '}
              <a href="https://www.whatsapp.com/legal/business-solution-terms/" target="_blank" rel="noopener noreferrer" className="text-[#00D95F] hover:underline">
                WhatsApp Business Solution Terms
              </a>{' '}
              and{' '}
              <a href="https://developers.facebook.com/policy/" target="_blank" rel="noopener noreferrer" className="text-[#00D95F] hover:underline">
                Meta Developer Policies
              </a>
            </p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
